import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Card,
  Alert,
  Badge,
  ListGroup,
  Spinner
} from 'react-bootstrap';
import {
  FaPlus,
  FaTrash,
  FaSave,
  FaTimes,
  FaInfoCircle
} from 'react-icons/fa';
import ObjectConfigApi from '../../../api/ObjectConfigApi';
import UserGroupApi from '../../../api/UserGroupApi';
import DynamicFieldInput from '../../common/DynamicFieldInput';
import LookupLayout from '../../layout/LookupLayout';

const AssignmentRuleForm = ({ show, onHide, onSave, editingRule, objects }) => {
  const [formData, setFormData] = useState({
    object_name: '',
    name: '',
    description: '',
    conditions: [],
    assign_type: 'user',
    assign_to: '',
    assignment_strategy: 'manual',
    strategy_config: {},
    is_active: true,
    priority: 0,
    completion_field: '',
    completion_values: []
  });

  const [fields, setFields] = useState([]);

  // Separate state for user and group selections to preserve when switching
  const [userSelection, setUserSelection] = useState({ id: '', label: '' });
  const [groupSelection, setGroupSelection] = useState({ id: '', label: '' });

  const [loadingFields, setLoadingFields] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const allOperators = {
    text: [
      { value: 'equals', label: 'Equals' },
      { value: 'not_equals', label: 'Not Equals' },
      { value: 'contains', label: 'Contains' },
      { value: 'not_contains', label: 'Does Not Contain' },
      { value: 'starts_with', label: 'Starts With' },
      { value: 'ends_with', label: 'Ends With' },
      { value: 'is_empty', label: 'Is Empty' },
      { value: 'is_not_empty', label: 'Is Not Empty' }
    ],
    number: [
      { value: 'equals', label: 'Equals' },
      { value: 'not_equals', label: 'Not Equals' },
      { value: 'greater_than', label: 'Greater Than' },
      { value: 'greater_than_equals', label: 'Greater Than or Equal' },
      { value: 'less_than', label: 'Less Than' },
      { value: 'less_than_equals', label: 'Less Than or Equal' },
      { value: 'is_empty', label: 'Is Empty' },
      { value: 'is_not_empty', label: 'Is Not Empty' }
    ],
    boolean: [
      { value: 'is_true', label: 'Is True' },
      { value: 'is_false', label: 'Is False' }
    ],
    picklist: [
      { value: 'equals', label: 'Equals' },
      { value: 'not_equals', label: 'Not Equals' },
      { value: 'is_empty', label: 'Is Empty' },
      { value: 'is_not_empty', label: 'Is Not Empty' }
    ],
    date: [
      { value: 'equals', label: 'Equals' },
      { value: 'not_equals', label: 'Not Equals' },
      { value: 'greater_than', label: 'After' },
      { value: 'greater_than_equals', label: 'On or After' },
      { value: 'less_than', label: 'Before' },
      { value: 'less_than_equals', label: 'On or Before' },
      { value: 'is_empty', label: 'Is Empty' },
      { value: 'is_not_empty', label: 'Is Not Empty' }
    ],
    lookup: [
      { value: 'equals', label: 'Equals' },
      { value: 'not_equals', label: 'Not Equals' },
      { value: 'is_empty', label: 'Is Empty' },
      { value: 'is_not_empty', label: 'Is Not Empty' }
    ]
  };

  // Load fields for selected object
  useEffect(() => {
    if (formData.object_name && show) {
      fetchObjectFields();
    }
  }, [formData.object_name, show]);

  // Debug completion values
  useEffect(() => {
    if (formData.completion_field) {
      const selectedField = fields.find(f => f.field_name === formData.completion_field);
      console.log('🔍 Completion field selected:', formData.completion_field);
      console.log('🔍 Available picklist values:', selectedField?.picklist_values);
      console.log('🔍 Current completion_values:', formData.completion_values);
    }
  }, [formData.completion_field, formData.completion_values, fields]);

  // Clear error when modal opens/closes
  useEffect(() => {
    if (!show) {
      setError('');
      setSaving(false);
    }
  }, [show]);

  // Populate form when editing
  useEffect(() => {
    if (editingRule) {
      console.log('=== EDITING RULE ===');
      console.log('Full editing rule:', editingRule);

      // Parse conditions if string
      let conditions = editingRule.conditions || [];
      if (typeof conditions === 'string') {
        try {
          conditions = JSON.parse(conditions);
        } catch (e) {
          console.error('Error parsing conditions:', e);
          conditions = [];
        }
      }

      // Parse strategy_config if string
      let strategyConfig = editingRule.strategy_config || {};
      if (typeof strategyConfig === 'string') {
        try {
          strategyConfig = JSON.parse(strategyConfig);
        } catch (e) {
          console.error('Error parsing strategy_config:', e);
          strategyConfig = {};
        }
      }

      // Parse completion_values if string
      let completionValues = editingRule.completion_values || [];
      if (typeof completionValues === 'string') {
        try {
          completionValues = JSON.parse(completionValues);
        } catch (e) {
          console.error('Error parsing completion_values:', e);
          completionValues = [];
        }
      }

      console.log('📝 Editing rule - completion_field:', editingRule.completion_field);
      console.log('📝 Editing rule - completion_values (parsed):', completionValues);

      // Get label from assign_to_details and set appropriate selection
      console.log('📝 Editing rule - assign_to_details:', editingRule.assign_to_details);
      console.log('📝 Editing rule - assign_type:', editingRule.assign_type);
      console.log('📝 Editing rule - assign_to:', editingRule.assign_to);

      if (editingRule.assign_to_details) {
        if (editingRule.assign_type === 'user') {
          const label = `${editingRule.assign_to_details.firstname || ''} ${editingRule.assign_to_details.lastname || ''}`.trim()
            || editingRule.assign_to_details.email || '';
          console.log('📝 Setting user selection:', { id: editingRule.assign_to, label });
          setUserSelection({ id: editingRule.assign_to || '', label });
          setGroupSelection({ id: '', label: '' }); // Clear group selection
        } else if (editingRule.assign_type === 'group') {
          const label = editingRule.assign_to_details.name || '';
          console.log('📝 Setting group selection:', { id: editingRule.assign_to, label });
          setGroupSelection({ id: editingRule.assign_to || '', label });
          setUserSelection({ id: '', label: '' }); // Clear user selection
        }
      } else {
        console.log('⚠️ No assign_to_details found in editingRule');
      }

      setFormData({
        object_name: editingRule.object_name || '',
        name: editingRule.name || '',
        description: editingRule.description || '',
        conditions: conditions,
        assign_type: editingRule.assign_type || 'user',
        assign_to: editingRule.assign_to || '',
        assignment_strategy: editingRule.assignment_strategy || 'manual',
        strategy_config: strategyConfig,
        is_active: editingRule.is_active !== false,
        priority: editingRule.priority || 0,
        completion_field: editingRule.completion_field || '',
        completion_values: completionValues
      });
    } else {
      // Reset form for new rule
      setUserSelection({ id: '', label: '' });
      setGroupSelection({ id: '', label: '' });
      setFormData({
        object_name: '',
        name: '',
        description: '',
        conditions: [],
        assign_type: 'user',
        assign_to: '',
        assignment_strategy: 'manual',
        strategy_config: {},
        is_active: true,
        priority: 0,
        completion_field: '',
        completion_values: []
      });
    }
  }, [editingRule, show]);

  const fetchObjectFields = async () => {
    if (!formData.object_name) return;

    setLoadingFields(true);
    setFields([]);
    try {
      console.log('Fetching fields for object:', formData.object_name);
      const response = await ObjectConfigApi.fetchConfigObjectByNameWithFields(formData.object_name);

      if (response?.success && response.data) {
        const fieldList = response.data.fields || [];
        const filteredFields = fieldList.filter(f => !f.is_deleted);

        // Map fields to use picklist_values_data (already included from backend)
        const fieldsWithPicklists = filteredFields.map((field) => {
          if (field.data_type === 'picklist' || field.data_type === 'multipicklist') {
            return {
              ...field,
              // Use picklist_values_data from backend if available, otherwise empty array
              picklist_values: field.picklist_values_data || []
            };
          }
          return field;
        });

        console.log('📦 Fields loaded with picklist values:', fieldsWithPicklists.filter(f => f.data_type === 'picklist' || f.data_type === 'multipicklist'));
        setFields(fieldsWithPicklists);
      } else {
        setError('Failed to fetch object fields');
      }
    } catch (err) {
      console.error('Error fetching fields:', err);
      setError('Failed to fetch object fields: ' + err.message);
    } finally {
      setLoadingFields(false);
    }
  };

  const handleAddCondition = () => {
    setFormData(prev => ({
      ...prev,
      conditions: [
        ...prev.conditions,
        { field: '', operator: 'equals', value: '' }
      ]
    }));
  };

  const handleRemoveCondition = (index) => {
    setFormData(prev => ({
      ...prev,
      conditions: prev.conditions.filter((_, i) => i !== index)
    }));
  };

  const handleConditionChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      conditions: prev.conditions.map((cond, i) =>
        i === index ? { ...cond, [field]: value } : cond
      )
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      // Validation
      if (!formData.object_name) {
        setError('Object is required');
        setSaving(false);
        return;
      }

      if (!formData.name || formData.name.trim() === '') {
        setError('Rule name is required');
        setSaving(false);
        return;
      }

      if (!formData.assign_type) {
        setError('Assign type is required');
        setSaving(false);
        return;
      }

      if (!formData.assign_to) {
        setError('Assign to (user or group) is required');
        setSaving(false);
        return;
      }

      if (!formData.assignment_strategy) {
        setError('Assignment strategy is required');
        setSaving(false);
        return;
      }

      // Validate completion tracking fields for load_based and least_active strategies
      if (formData.assign_type === 'group' &&
        (formData.assignment_strategy === 'load_based' || formData.assignment_strategy === 'least_active')) {
        if (!formData.completion_field || formData.completion_field.trim() === '') {
          setError(`Completion field is required for ${formData.assignment_strategy === 'load_based' ? 'Load Based' : 'Least Active'} strategy`);
          setSaving(false);
          return;
        }
        if (!formData.completion_values || formData.completion_values.length === 0) {
          setError('At least one completion value must be selected');
          setSaving(false);
          return;
        }
      }

      // Validate conditions
      for (let i = 0; i < formData.conditions.length; i++) {
        const cond = formData.conditions[i];
        if (!cond.field) {
          setError(`Condition ${i + 1}: Field is required`);
          setSaving(false);
          return;
        }
        if (!cond.operator) {
          setError(`Condition ${i + 1}: Operator is required`);
          setSaving(false);
          return;
        }
        // Value is not required for is_empty, is_not_empty, is_true, is_false operators
        if (!['is_empty', 'is_not_empty', 'is_true', 'is_false'].includes(cond.operator) &&
          !cond.value && cond.value !== 0 && cond.value !== false) {
          setError(`Condition ${i + 1}: Value is required`);
          setSaving(false);
          return;
        }
      }

      // Submit
      const result = await onSave(formData, setError);

      if (result && !result.success) {
        setSaving(false);
      }
    } catch (err) {
      console.error('Form submit error:', err);
      setError('An error occurred while saving');
      setSaving(false);
    }
  };

  const getFieldByName = (fieldName) => {
    return fields.find(f => f.field_name === fieldName);
  };

  const needsValueInput = (operator) => {
    return !['is_empty', 'is_not_empty', 'is_true', 'is_false'].includes(operator);
  };

  const getOperatorsForField = (fieldName) => {
    if (!fieldName) return allOperators.text;

    const field = getFieldByName(fieldName);
    if (!field) return allOperators.text;

    const dataType = field.data_type?.toLowerCase();

    if (dataType === 'boolean' || dataType === 'checkbox') {
      return allOperators.boolean;
    }
    if (dataType === 'number' || dataType === 'integer' || dataType === 'currency' ||
      dataType === 'percent' || dataType === 'rollupsummary') {
      return allOperators.number;
    }
    if (dataType === 'picklist' || dataType === 'multipicklist') {
      return allOperators.picklist;
    }
    if (dataType === 'date' || dataType === 'datetime') {
      return allOperators.date;
    }
    if (dataType === 'lookup' || dataType === 'reference' || field.is_reference) {
      return allOperators.lookup;
    }

    return allOperators.text;
  };

  const handleAssignToChange = (id, label) => {
    console.log('Assign to changed:', { id, label, type: formData.assign_type });
    // Update the appropriate selection based on current assign_type
    if (formData.assign_type === 'user') {
      setUserSelection({ id, label });
      setFormData(prev => ({ ...prev, assign_to: id }));
    } else {
      setGroupSelection({ id, label });
      setFormData(prev => ({ ...prev, assign_to: id }));
    }
  };

  const handleAssignTypeChange = (newType) => {
    console.log('Assign type changed to:', newType);
    // Restore the appropriate selection when switching back
    const newAssignTo = newType === 'user' ? userSelection.id : groupSelection.id;

    console.log('Restored assign_to:', newAssignTo);
    console.log('Current selections:', { userSelection, groupSelection });

    setFormData(prev => ({
      ...prev,
      assign_type: newType,
      assign_to: newAssignTo,
      // Force manual strategy for user assignments
      assignment_strategy: newType === 'user' ? 'manual' : prev.assignment_strategy
    }));
  };

  return (
    <Modal show={show} onHide={onHide} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>
          {editingRule ? 'Edit Assignment Rule' : 'Create Assignment Rule'}
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {/* Basic Information */}
          <Card className="mb-3">
            <Card.Header>
              <strong className='text-white'>Basic Information</strong>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Object *</Form.Label>
                    <Form.Select
                      value={formData.object_name}
                      onChange={(e) => {
                        setFormData(prev => ({
                          ...prev,
                          object_name: e.target.value,
                          conditions: []
                        }));
                      }}
                      required
                      disabled={!!editingRule}
                    >
                      <option value="">-- Select an Object --</option>
                      {objects.map(obj => (
                        <option key={obj.name} value={obj.name}>
                          {obj.label}
                        </option>
                      ))}
                    </Form.Select>
                    {editingRule && (
                      <Form.Text className="text-muted">
                        Object cannot be changed when editing
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Rule Name *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="e.g., Assign High Priority Leads"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      placeholder="e.g., Automatically assign high-priority leads to sales team"
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Priority</Form.Label>
                    <Form.Control
                      type="number"
                      placeholder="0"
                      value={formData.priority}
                      onChange={(e) => setFormData(prev => ({ ...prev, priority: parseInt(e.target.value) || 0 }))}
                      min="0"
                    />
                    <Form.Text className="text-muted">
                      Higher priority rules execute first
                    </Form.Text>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Check
                      type="checkbox"
                      label="Active (Rule will be applied)"
                      checked={formData.is_active}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                      className="mt-4"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Assignment Configuration */}
          <Card className="mb-3">
            <Card.Header>
              <strong>Assignment Configuration *</strong>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Assign Type *</Form.Label>
                    <Form.Select
                      value={formData.assign_type}
                      onChange={(e) => handleAssignTypeChange(e.target.value)}
                      required
                    >
                      <option value="user">User</option>
                      <option value="group">Group</option>
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Assign To *</Form.Label>
                    {formData.assign_type === 'user' ? (
                      <LookupLayout
                        key="user-lookup"
                        objectName="user"
                        searchFields="firstname,lastname,email"
                        displayFields="firstname,lastname,email"
                        value={userSelection.id}
                        defaultLabel={userSelection.label}
                        onChange={handleAssignToChange}
                        placeholder="Search users..."
                        isRequired={true}
                      />
                    ) : (
                      <LookupLayout
                        key="group-lookup"
                        objectName="user_group"
                        searchFields="name"
                        displayFields="name"
                        value={groupSelection.id}
                        defaultLabel={groupSelection.label}
                        onChange={handleAssignToChange}
                        placeholder="Search groups..."
                        isRequired={true}
                      />
                    )}
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Assignment Strategy *</Form.Label>
                    <Form.Select
                      value={formData.assignment_strategy}
                      onChange={(e) => setFormData(prev => ({ ...prev, assignment_strategy: e.target.value }))}
                      required
                      disabled={formData.assign_type === 'user'}
                    >
                      <option value="manual">Manual</option>
                      <option value="round_robin">Round Robin</option>
                      <option value="load_based">Load Based</option>
                      <option value="least_active">Least Active</option>
                    </Form.Select>
                    <Form.Text className="text-muted">
                      {formData.assign_type === 'user' && 'User assignments always use Manual strategy'}
                      {formData.assign_type === 'group' && formData.assignment_strategy === 'round_robin' && 'Rotate assignments among group members'}
                      {formData.assign_type === 'group' && formData.assignment_strategy === 'load_based' && 'Assign based on current workload'}
                      {formData.assign_type === 'group' && formData.assignment_strategy === 'least_active' && 'Assign to least recently assigned user'}
                      {formData.assign_type === 'group' && formData.assignment_strategy === 'manual' && 'Direct assignment to group'}
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Completion Tracking Configuration */}
          {formData.assign_type === 'group' &&
            (formData.assignment_strategy === 'load_based' || formData.assignment_strategy === 'least_active') && (
              <Card className="mb-3">
                <Card.Header>
                  <strong>Completion Tracking Configuration</strong>
                  <Badge bg="info" className="ms-2">Required for {formData.assignment_strategy === 'load_based' ? 'Load Based' : 'Least Active'}</Badge>
                </Card.Header>
                <Card.Body>
                  <Alert variant="info" className="mb-3">
                    <FaInfoCircle className="me-2" />
                    <small>
                      Select which field indicates when a record is completed. This helps the system track active workload and assign to the right team member.
                    </small>
                  </Alert>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Completion Field *</Form.Label>
                        <Form.Select
                          value={formData.completion_field}
                          onChange={(e) => {
                            setFormData(prev => ({
                              ...prev,
                              completion_field: e.target.value,
                              completion_values: [] // Reset values when field changes
                            }));
                          }}
                          required
                        >
                          <option value="">-- Select a Picklist Field --</option>
                          {fields
                            .filter(f => f.data_type === 'picklist' || f.data_type === 'multipicklist')
                            .map(field => (
                              <option key={field.field_name} value={field.field_name}>
                                {field.field_label || field.field_name}
                              </option>
                            ))}
                        </Form.Select>
                        <Form.Text className="text-muted">
                          Select the picklist field that indicates record status (e.g., Status, Stage)
                        </Form.Text>
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Completed Values *</Form.Label>
                        <Form.Select
                          multiple
                          value={formData.completion_values}
                          onChange={(e) => {
                            const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
                            console.log('📋 Selected completion values:', selectedOptions);
                            setFormData(prev => ({ ...prev, completion_values: selectedOptions }));
                          }}
                          style={{ minHeight: '120px' }}
                          required
                          disabled={!formData.completion_field}
                        >
                          {formData.completion_field && fields
                            .find(f => f.field_name === formData.completion_field)
                            ?.picklist_values
                            ?.filter(pv => pv.is_active)
                            ?.map(picklistValue => {
                              // Use label as the value since that's what gets stored in the actual record
                              const optionValue = picklistValue.label || picklistValue.value;
                              return (
                                <option key={picklistValue.id} value={optionValue}>
                                  {picklistValue.label}
                                </option>
                              );
                            })}
                        </Form.Select>
                        <Form.Text className="text-muted">
                          {formData.completion_field
                            ? 'Hold Ctrl/Cmd to select multiple values that indicate completion (e.g., Closed, Won, Lost)'
                            : 'Please select a completion field first'}
                        </Form.Text>
                        {formData.completion_values && formData.completion_values.length > 0 && (
                          <Form.Text className="d-block mt-1 text-success">
                            ✓ {formData.completion_values.length} value{formData.completion_values.length > 1 ? 's' : ''} selected
                          </Form.Text>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>

                  {formData.completion_values.length > 0 && (
                    <Alert variant="success" className="mt-2 mb-0">
                      <small>
                        <strong>Completion Logic:</strong> Records where <code>{formData.completion_field}</code> equals{' '}
                        {formData.completion_values.map((val, idx) => (
                          <span key={val}>
                            <code>{val}</code>
                            {idx < formData.completion_values.length - 1 ? ' or ' : ''}
                          </span>
                        ))} will be marked as completed and excluded from workload calculations.
                      </small>
                    </Alert>
                  )}
                </Card.Body>
              </Card>
            )}

          {/* Conditions */}
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <strong>Assignment Conditions</strong>
              {formData.object_name && !loadingFields && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleAddCondition}
                  disabled={loadingFields || !formData.object_name}
                >
                  <FaPlus className="me-1" />
                  Add Condition
                </Button>
              )}
            </Card.Header>
            <Card.Body>
              {!formData.object_name ? (
                <div className="text-center py-4 text-muted">
                  <FaInfoCircle size={32} className="mb-2" />
                  <p>Please select an object first to add conditions.</p>
                </div>
              ) : loadingFields ? (
                <div className="text-center py-3">
                  <Spinner animation="border" size="sm" />
                  <span className="ms-2">Loading fields...</span>
                </div>
              ) : formData.conditions.length === 0 ? (
                <div className="text-center py-4">
                  <FaInfoCircle size={48} className="mb-3 text-muted" />
                  <h6 className="text-muted">No conditions added yet</h6>
                  <p className="text-muted small">
                    Add conditions to specify when this rule should apply.
                    <br />
                    Leave empty to always apply this rule.
                  </p>
                  <Button
                    variant="primary"
                    onClick={handleAddCondition}
                    className="mt-2"
                  >
                    <FaPlus className="me-1" />
                    Add First Condition
                  </Button>
                </div>
              ) : (
                <ListGroup>
                  {formData.conditions.map((condition, index) => (
                    <ListGroup.Item key={index}>
                      <Row className="align-items-center">
                        <Col md={1}>
                          <Badge bg="secondary">#{index + 1}</Badge>
                        </Col>
                        <Col md={3}>
                          <Form.Label className="small mb-1">Field</Form.Label>
                          <Form.Select
                            size="sm"
                            value={condition.field}
                            onChange={(e) => {
                              const newField = e.target.value;
                              handleConditionChange(index, 'field', newField);
                              const operators = getOperatorsForField(newField);
                              if (operators.length > 0) {
                                handleConditionChange(index, 'operator', operators[0].value);
                              }
                              handleConditionChange(index, 'value', '');
                            }}
                            required
                          >
                            <option value="">-- Select Field --</option>
                            {fields.map(field => (
                              <option key={field.field_name} value={field.field_name}>
                                {field.field_label || field.field_name}
                              </option>
                            ))}
                          </Form.Select>
                        </Col>
                        <Col md={3}>
                          <Form.Label className="small mb-1">Operator</Form.Label>
                          <Form.Select
                            size="sm"
                            value={condition.operator}
                            onChange={(e) => handleConditionChange(index, 'operator', e.target.value)}
                            required
                          >
                            {getOperatorsForField(condition.field).map(op => (
                              <option key={op.value} value={op.value}>
                                {op.label}
                              </option>
                            ))}
                          </Form.Select>
                        </Col>
                        <Col md={4}>
                          <Form.Label className="small mb-1">Value</Form.Label>
                          {needsValueInput(condition.operator) ? (
                            <DynamicFieldInput
                              field={getFieldByName(condition.field)}
                              value={condition.value}
                              displayValue={condition.displayValue}
                              onChange={(value, displayValue) => {
                                handleConditionChange(index, 'value', value);
                                if (displayValue !== undefined) {
                                  handleConditionChange(index, 'displayValue', displayValue);
                                }
                              }}
                              operator={condition.operator}
                              size="sm"
                              placeholder="Value"
                            />
                          ) : (
                            <Form.Control
                              size="sm"
                              type="text"
                              value="N/A"
                              disabled
                            />
                          )}
                        </Col>
                        <Col md={1}>
                          <Form.Label className="small mb-1">&nbsp;</Form.Label>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleRemoveCondition(index)}
                            className="w-100"
                          >
                            <FaTrash />
                          </Button>
                        </Col>
                      </Row>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}

              {formData.conditions.length > 0 && (
                <div className="mt-3">
                  <Alert variant="info" className="mb-0">
                    <small>
                      <FaInfoCircle className="me-2" />
                      All conditions must be met (AND logic) for this rule to apply.
                    </small>
                  </Alert>
                </div>
              )}
            </Card.Body>
          </Card>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={saving}>
            <FaTimes className="me-1" />
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={saving}>
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              <>
                <FaSave className="me-1" />
                {editingRule ? 'Update Rule' : 'Create Rule'}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default AssignmentRuleForm;
